import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Server } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { useOpsServices, type OpsServiceRow } from '@/api/operations';
import { opsServiceColumns } from '@/pages/operations/components/ops-service-columns';
import { OpsRavenRebindDialog } from './ops-raven-rebind-dialog';

const isRaven = (r: OpsServiceRow) => r.provider?.name?.toLowerCase() === 'raven';

/**
 * Org detail Services tab — reuses the cross-org services query filtered by
 * org_id. Rows link to /operations/services/:id (the full service detail page).
 * No pagination — orgs with very large service counts will be addressed if
 * needed; at current scale a single page of 100 is sufficient.
 *
 * Raven rows get a "Re-bind credential" action (Raven credentials are shared at
 * org level; the backend enforces the Tech role).
 */
export function OpsOrgServicesTab({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const [rebindTarget, setRebindTarget] = useState<OpsServiceRow | null>(null);

  const { data, isLoading, isError, refetch } = useOpsServices({
    org_id: Number(orgId),
    per_page: 100,
  });

  const rows = useMemo(() => data?.results ?? [], [data]);
  const ravenRows = useMemo(() => rows.filter(isRaven), [rows]);

  const columns = useMemo<ColumnDef<OpsServiceRow, unknown>[]>(
    () => [
      ...opsServiceColumns,
      {
        id: 'actions',
        header: '',
        cell: ({ row }) =>
          isRaven(row.original) ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setRebindTarget(row.original);
              }}
            >
              <KeyRound size={14} /> Re-bind
            </Button>
          ) : null,
      },
    ],
    [],
  );

  if (isError) return <TabError title="Failed to load services" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading rows={6} />;

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(r) => r.id}
        onRowClick={(row) => navigate(`/operations/services/${row.id}`)}
        emptyState={
          <EmptyState
            icon={<Server size={24} />}
            title="No services"
            description="This organization has no provisioned services."
          />
        }
      />
      {rebindTarget && (
        <OpsRavenRebindDialog
          key={rebindTarget.id}
          service={rebindTarget}
          ravenServices={ravenRows}
          open
          onOpenChange={(o) => { if (!o) setRebindTarget(null); }}
        />
      )}
    </>
  );
}
